import * as React from "react";

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shared/components/primitives/sheet";
import { cn } from "@/shared/lib/utils";

type DrawerSide = "right" | "left";
type DrawerSize = "sm" | "md" | "lg" | "xl";

export interface ColocaDrawerProps {
  /**
   * Element that opens the drawer — typically a `<ColocaButton>`. The drawer wires up the click handler
   * automatically; do not add your own `onClick` to the trigger. Omit when the drawer is opened
   * programmatically via `open` / `onOpenChange`.
   */
  trigger?: React.ReactElement;
  /** Short title rendered at the top of the panel. Keep to one line. */
  title: React.ReactNode;
  /** Supporting text shown below the title. Use for context, instructions, or summary. */
  description?: React.ReactNode;
  /**
   * Fixed strip between the header and the scrolling body — a wizard's step indicator, a filter row, a
   * persistent notice. Use it for anything that must stay visible while the content moves under it; anything
   * else belongs in `children`.
   *
   * It is also the only way to add such a strip without disturbing `pinLastChild`, which keys off the body's
   * DOM shape: a toolbar placed inside `children` would be a second root element and silently switch the pin
   * off for everything below it.
   */
  toolbar?: React.ReactNode;
  /** Main body content — sections, lists, forms, or any other React nodes. Scrolls vertically when long. */
  children?: React.ReactNode;
  /**
   * Sticky footer area for primary/secondary actions. Wrap primary actions in `<ColocaButton
   * variant="primary">`. Use `<ColocaDrawerClose>` to wire up a cancel button.
   */
  footer?: React.ReactNode;
  /** Edge the panel slides in from. Defaults to `"right"`. */
  side?: DrawerSide;
  /**
   * Panel width on the `sm` breakpoint and up. - `"sm"` — `max-w-sm` (24rem). Use for short detail summaries.
   * - `"md"` — `max-w-md` (28rem). Default. Suitable for most settings or detail panels. - `"lg"` —
   * `max-w-2xl` (42rem). Use for content-heavy lists or stacked cards. - `"xl"` — `max-w-4xl` (56rem).
   * Reserved for dense side-by-side layouts. Below the `sm` breakpoint the panel always occupies 75% of the
   * viewport width.
   */
  size?: DrawerSize;
  /**
   * Whether to render the × close button in the top-right corner. Defaults to `true`. Turn off only when the
   * panel must be dismissed through an explicit footer action.
   */
  showCloseButton?: boolean;
  /**
   * Pins the body's last child to the bottom edge — `mt-auto` plus `sticky bottom-0` and a full-bleed
   * background — but **only when the body renders a single root element**.
   *
   * It exists for wizards that embed their own action row instead of using `footer`, and it keeps that row
   * visible while the content above scrolls. Defaults to `true` for compatibility with the drawers written
   * before the prop existed.
   *
   * **Pass `false` whenever the body's last child is CONTENT rather than an action row** — a form section, a
   * card, an accordion. Because the rule is triggered by DOM shape rather than by intent, a single-child body
   * gets it whether or not it wants it, and the result is a block that drifts to the bottom of a tall panel
   * (`mt-auto`) and floats over the scrolling fields on a short one (`sticky`). A body that renders several
   * root elements never triggers it either way.
   */
  pinLastChild?: boolean;
  /**
   * Renders the panel's own backdrop even when the drawer is **nested inside another dialog**. Defaults to
   * `false`, which is Base UI's own behaviour.
   *
   * Base UI decides nesting from React context and suppresses the nested backdrop (`enabled: forceRender ||
   * !nested`) so the page is not veiled twice. That default is right for a small confirmation centred over
   * its parent, and wrong for a full-height side panel that OVERLAPS its parent: the parent's card is opaque,
   * so it keeps full luminance and the panel reads as pasted onto a surface still competing for attention,
   * rather than as the layer in front of it.
   *
   * Turning it on restores the depth ladder — page (two scrims) < parent (one scrim, blurred) < panel
   * (untouched) — with the very scrim a standalone drawer already paints, so a nested drawer goes on looking
   * like a drawer.
   *
   * It cannot be replaced by dimming the PARENT instead. Only the parent's _popup_ carries a nesting signal
   * (`data-nested-dialog-open` / `--nested-dialogs`); its backdrop carries none, so that route reaches the
   * middle layer only, and darkening it enough to notice renders the parent darker than the page behind it.
   */
  showBackdropWhenNested?: boolean;
  /** Controlled open state. Pair with `onOpenChange` to manage the drawer from outside. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Extra classes appended to the panel content wrapper. */
  className?: string;
}

/** Stretch the single-root body to fill the panel. Harmless on its own — it only affects height. */
const BODY_FILL =
  "[&>*:only-child]:flex [&>*:only-child]:min-h-full [&>*:only-child]:flex-1 [&>*:only-child]:flex-col";

/**
 * Pin that body's last child to the bottom edge. This is the part that must be opt-out-able.
 *
 * `-mx-4` full-bleeds the row against the body's horizontal padding and `p-4` puts that padding back inside
 * it. There is deliberately no `-mb-*` counterpart: `sticky bottom-0` anchors the row to the scrollport's
 * CONTENT edge, so a negative bottom margin never reaches the padding — it only shortens the flow. The body
 * drops its bottom padding instead when the pin is on (see the body's className), which is what actually
 * makes the row own the edge. It carried `-mb-4` for a long time and the 16px band under the bar, with the
 * content sliding visibly through it, was the result.
 */
const BODY_PIN =
  "[&>*:only-child>:last-child]:sticky [&>*:only-child>:last-child]:bottom-0 [&>*:only-child>:last-child]:mt-auto [&>*:only-child>:last-child]:-mx-4 [&>*:only-child>:last-child]:bg-popover [&>*:only-child>:last-child]:p-4";

const SIZE_CLASS: Record<DrawerSize, string> = {
  sm: "data-[side=right]:sm:max-w-sm data-[side=left]:sm:max-w-sm",
  md: "data-[side=right]:sm:max-w-md data-[side=left]:sm:max-w-md",
  lg: "data-[side=right]:sm:max-w-2xl data-[side=left]:sm:max-w-2xl",
  xl: "data-[side=right]:sm:max-w-4xl data-[side=left]:sm:max-w-4xl",
};

/**
 * Side panel for secondary tasks, detail views, and configuration flows that benefit from staying anchored to
 * the page they originate from. Slides in from the right by default.
 *
 * Prefer `ColocaDrawer` over `ColocaDialog` when: - The content is taller than a modal can comfortably
 * handle. - The user benefits from peripheral context (the underlying page stays visible behind the
 * backdrop). - The flow is non-destructive — for irreversible actions use `ColocaAlertDialog`.
 */
export function ColocaDrawer({
  trigger,
  title,
  description,
  toolbar,
  children,
  footer,
  side = "right",
  size = "md",
  showCloseButton = true,
  pinLastChild = true,
  showBackdropWhenNested = false,
  open,
  onOpenChange,
  className,
}: ColocaDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {trigger && <SheetTrigger nativeButton render={trigger} />}
      <SheetContent
        side={side}
        showCloseButton={showCloseButton}
        forceBackdrop={showBackdropWhenNested}
        className={cn(
          // Mobile: full viewport width. The size cap kicks in at the sm
          // breakpoint via SIZE_CLASS below.
          "data-[side=right]:w-full data-[side=left]:w-full",
          SIZE_CLASS[size],
          "flex flex-col gap-0",
          className,
        )}
      >
        <SheetHeader className="border-b border-border">
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        {toolbar && <div className="shrink-0 border-b border-border p-4">{toolbar}</div>}
        {/*
          When children is a single root element (the common case for multi-step wizards), stretch it to
          fill the body and — unless the consumer opts out with `pinLastChild={false}` — sticky-pin its last
          child to the bottom edge. That keeps embedded action rows (Cerrar / Continuar) visible while the
          content above scrolls, without touching the consumer component.

          The trigger is DOM shape, not intent, so it cannot tell an action row from a content block: see
          `pinLastChild`. A body that renders several root elements never gets either treatment.
        */}
        {/*
          `BODY_FILL` is gated on `pinLastChild` as well, because the two only make sense together: filling
          the body exists to give the pin something to push an action row against. Without a pin it has no
          job, and it manufactures a bug — a body shorter than the panel gets stretched, so the slack lands
          after its last child and reads as generous spacing, then evaporates the moment the content grows
          past the panel, leaving the last block flush against whatever is below. That is why the COP
          deposit modal looked well spaced until an amount was typed.

          Note this keys off `pinLastChild`, NOT off `footer`. `ColocaDialog` passes `pinLastChild={!footer}`,
          so on that path the two coincide; a `ColocaDrawer` used directly defaults to `true` whether or not
          it also renders a `footer`, and several do. Pass `pinLastChild={false}` when the body's last child
          is content rather than an action row — see `ColocaDrawerProps.pinLastChild`.
        */}
        <div
          data-slot="drawer-body"
          className={cn(
            "flex flex-1 flex-col overflow-y-auto px-4 pt-4",
            // The bottom padding is part of the same decision as the pin, so it is stated here.
            //
            // Pinned: NO bottom padding. `sticky bottom-0` anchors the row to the scrollport's content edge,
            // so any padding below it stays live and the scrolling content slides through that band, under a
            // bar whose whole job is to own the edge. Measured in `PinnedActionRow`'s play test.
            //
            // Unpinned: `pb-6` rather than the uniform `p-4` — the footer draws a rule directly above
            // itself, and 16px is not enough separation for the last block to read as its own.
            pinLastChild ? [BODY_FILL, BODY_PIN] : "pb-6",
          )}
        >
          {children}
        </div>
        {footer && <SheetFooter className="border-t border-border">{footer}</SheetFooter>}
      </SheetContent>
    </Sheet>
  );
}

export { SheetClose as ColocaDrawerClose };
